<template>
  <div class="auth-container">
    <div class="auth-card">
      <div>
        <div class="logo-container">
          <img src="@/assets/query-img.svg" :alt="$t('auth.common.logoAlt')" />
        </div>
        <router-link to="/dashboardPage" class="back-link">{{ $t('auth.changePassword.backToDashboard') }}</router-link>
        <h5 class="form-title">{{ $t('auth.changePassword.title') }}</h5>

        <form v-if="!passwordChanged" @submit.prevent="changePassword" class="auth-form">
          <!-- Current Password -->
          <div class="form-field">
            <label class="input-label" for="currentPassword">{{ $t('auth.changePassword.currentLabel') }}</label>
            <div class="password-container">
              <input
                :type="showCurrentPassword ? 'text' : 'password'"
                class="form-input"
                id="currentPassword"
                v-model="currentPassword"
                required
                autocomplete="current-password"
                :class="{ 'error-input': currentPasswordError }"
                :placeholder="$t('auth.changePassword.currentPlaceholder')"
              />
              <img
                class="eye-icon"
                src="@/assets/eye-icon.svg"
                :alt="$t('auth.common.togglePasswordAlt')"
                @click="toggleCurrentPassword"
              />
            </div>
            <div v-if="currentPasswordError" class="error-message">
              {{ currentPasswordErrorMessage }}
            </div>
          </div>

          <!-- New Password -->
          <div class="form-field">
            <label class="input-label" for="newPassword">{{ $t('auth.changePassword.newLabel') }}</label>
            <div class="password-container">
              <input
                :type="showNewPassword ? 'text' : 'password'"
                class="form-input"
                id="newPassword"
                v-model="newPassword"
                required
                autocomplete="new-password"
                :class="{ 'error-input': newPasswordError }"
                :placeholder="$t('auth.changePassword.newPlaceholder')"
              />
              <img
                class="eye-icon"
                src="@/assets/eye-icon.svg"
                :alt="$t('auth.common.togglePasswordAlt')"
                @click="toggleNewPassword"
              />
            </div>
          </div>

          <!-- Confirm New Password -->
          <div class="form-field">
            <label class="input-label" for="confirmNewPassword">{{ $t('auth.changePassword.confirmLabel') }}</label>
            <div class="password-container">
              <input
                :type="showConfirmNewPassword ? 'text' : 'password'"
                class="form-input"
                id="confirmNewPassword"
                v-model="confirmNewPassword"
                required
                autocomplete="new-password"
                :class="{ 'error-input': newPasswordError }"
                :placeholder="$t('auth.changePassword.confirmPlaceholder')"
              />
              <img
                class="eye-icon"
                src="@/assets/eye-icon.svg"
                :alt="$t('auth.common.togglePasswordAlt')"
                @click="toggleConfirmNewPassword"
              />
            </div>
            <div v-if="newPasswordError" class="error-message">
              {{ newPasswordErrorMessage }}
            </div>
          </div>

          <button type="submit" class="auth-button primary-button" :disabled="isSubmitting">
            {{ isSubmitting ? $t('auth.changePassword.submitting') : $t('auth.changePassword.submit') }}
          </button>
        </form>

        <div v-else class="success-container">
          <div class="success-message">
            {{ $t('auth.changePassword.success') }}
          </div>
          <router-link to="/dashboardPage" class="auth-button primary-button">
            {{ $t('auth.changePassword.backToDashboardCta') }}
          </router-link>
        </div>
      </div>
      <div class="copyright">{{ $t('auth.common.copyright') }}</div>
    </div>
  </div>
</template>

<script>
import { supabase } from '@/supabase/supabaseClient';
import { validatePassword } from '@/utils/passwordValidation';

const API_URL = process.env.VUE_APP_API_URL || 'https://api.query-juriste.com';

export default {
  name: 'ChangePassword',
  data() {
    return {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmNewPassword: false,
      currentPasswordError: false,
      currentPasswordErrorMessage: '',
      newPasswordError: false,
      newPasswordErrorMessage: '',
      isSubmitting: false,
      passwordChanged: false
    };
  },
  methods: {
    toggleCurrentPassword() {
      this.showCurrentPassword = !this.showCurrentPassword;
    },
    toggleNewPassword() {
      this.showNewPassword = !this.showNewPassword;
    },
    toggleConfirmNewPassword() {
      this.showConfirmNewPassword = !this.showConfirmNewPassword;
    },
    async changePassword() {
      // Reset errors
      this.currentPasswordError = false;
      this.currentPasswordErrorMessage = '';
      this.newPasswordError = false;
      this.newPasswordErrorMessage = '';

      // Validate new password strength (12 char + 4 classes, conforme CNIL 2017-012)
      const pwdCheck = validatePassword(this.newPassword);
      if (!pwdCheck.valid) {
        this.newPasswordError = true;
        this.newPasswordErrorMessage = pwdCheck.errors.join(' · ');
        return;
      }

      // Validate password match
      if (this.newPassword !== this.confirmNewPassword) {
        this.newPasswordError = true;
        this.newPasswordErrorMessage = this.$t('auth.changePassword.errors.mismatch');
        return;
      }

      // Validate new password is different from current
      if (this.newPassword === this.currentPassword) {
        this.newPasswordError = true;
        this.newPasswordErrorMessage = this.$t('auth.changePassword.errors.sameAsOld');
        return;
      }

      this.isSubmitting = true;

      try {
        // Récupérer le JWT de la session courante
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;

        if (!token) {
          throw new Error(this.$t('auth.changePassword.errors.sessionExpired'));
        }

        // Vérification de l'ancien mot de passe + mise à jour côté serveur
        const response = await fetch(`${API_URL}/copilot/api/changePassword`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            currentPassword: this.currentPassword,
            newPassword: this.newPassword
          })
        });

        const result = await response.json();

        if (response.status === 400 && result.message === 'Mot de passe actuel incorrect') {
          this.currentPasswordError = true;
          this.currentPasswordErrorMessage = this.$t('auth.changePassword.errors.currentIncorrect');
          return;
        }

        if (!response.ok) {
          throw new Error(result.message || this.$t('auth.changePassword.errors.generic'));
        }

        // Success
        this.passwordChanged = true;
        this.$toast.success(this.$t('auth.changePassword.success'));

      } catch (error) {
        console.error('Error changing password:', error);
        this.newPasswordError = true;
        this.newPasswordErrorMessage = error.message || this.$t('auth.changePassword.errors.generic');
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500&display=swap');

.auth-container {
  display: flex;
  justify-content: center;
  background: url("@/assets/sign-In-bg.png");
  background-size: cover;
  background-position: center;
  height: 100vh;
  font-family: 'Roboto', sans-serif;
}

.auth-card {
  width: 500px;
  padding: 0 80px;
  background-color: white;
  overflow: auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.logo-container {
  width: 100%;
  height: 130px;
  background: black;
  border-radius: 0 0 20px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.back-link {
  display: inline-block;
  margin-top: 20px;
  color: #6c63ff;
  text-decoration: none;
  font-size: 14px;
}

.back-link:hover {
  text-decoration: underline;
}

.form-title {
  text-align: left;
  font-weight: bold;
  margin-top: 20px;
  font-size: 18px;
}

.auth-form {
  margin-top: 20px;
}

.form-field {
  margin-bottom: 20px;
}

.input-label {
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  text-align: left;
}

.form-input {
  width: 100%;
  padding: 10px 15px;
  border: 1px solid #ccc;
  border-radius: 5px;
  background-color: #f2f2f2;
  height: 40px;
}

.password-container {
  position: relative;
}

.eye-icon {
  position: absolute;
  top: 12px;
  right: 15px;
  cursor: pointer;
}

.error-input {
  border-color: #ff4444;
}

.error-message {
  color: #ff4444;
  font-size: 12px;
  margin-top: 5px;
}

.auth-button {
  width: 100%;
  padding: 10px 0;
  border: none;
  border-radius: 15px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 15px;
  text-align: center;
}

.primary-button {
  background-color: #6c63ff;
  color: white;
  display: inline-block;
  text-decoration: none;
}

.primary-button:hover {
  background-color: #5a54d1;
}

.primary-button:disabled {
  background-color: #a5a0ff;
  cursor: not-allowed;
}

.success-container {
  margin-top: 30px;
  text-align: center;
}

.success-message {
  padding: 15px;
  background-color: #d4edda;
  color: #155724;
  border-radius: 5px;
  margin-bottom: 20px;
}

.copyright {
  font-size: 12px;
  margin-bottom: 40px;
  text-align: center;
  color: #888;
}

@media (max-width: 768px) {
  .auth-card {
    width: 90%;
    padding: 0 30px;
  }

  .logo-container {
    height: 100px;
  }
}
</style>
